import React, { createContext, useContext, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBusiness } from "@/context/BusinessContext";

const LearningContext = createContext(null);

export function LearningProvider({ children }) {
  const { user } = useAuth();
  const { business } = useBusiness();
  const [popup, setPopup] = useState(null);

  const isBusiness = user && (user.role === "manager" || user.role === "agent");
  const enabled = !!isBusiness && (business ? business.learning_enabled !== false : true);

  const learn = useCallback(
    async (record) => {
      if (!enabled || !record) return;
      const src = record.detected_language;
      const tgt = record.target_lang;
      if (!["en", "ar"].includes(src) || !["en", "ar"].includes(tgt) || src === tgt) return;
      if (!record.source_text || !record.translation) return;
      try {
        const { data } = await api.post("/learning/phrases", {
          original_text: record.source_text,
          translation: record.translation,
          direction: `${src}-to-${tgt}`,
          context: "translation",
        });
        setPopup({ ...data, _shownAt: Date.now() });
      } catch (e) {
        console.debug("Learning phrase log skipped (non-blocking):", e?.message);
      }
    },
    [enabled]
  );

  const dismiss = useCallback(() => setPopup(null), []);
  const updatePopup = useCallback((patch) => setPopup((p) => (p ? { ...p, ...patch } : p)), []);

  return (
    <LearningContext.Provider value={{ enabled, learn, popup, dismiss, updatePopup }}>
      {children}
    </LearningContext.Provider>
  );
}

export const useLearning = () =>
  useContext(LearningContext) || {
    enabled: false,
    learn: () => {},
    popup: null,
    dismiss: () => {},
    updatePopup: () => {},
  };
